import { QueryClientProvider } from "@tanstack/solid-query";
import { SolidQueryDevtools } from "@tanstack/solid-query-devtools";
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  Scripts,
  useRouter,
} from "@tanstack/solid-router";
import { TanStackRouterDevtools } from "@tanstack/solid-router-devtools";

import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";

import { createEffect, onCleanup, Suspense } from "solid-js";
import { HydrationScript, isServer } from "solid-js/web";
import { AuthProvider, useAuth } from "../lib/auth";
import {
  applyThemePreference,
  initializeThemePreference,
  THEME_INIT_SCRIPT,
  useTheme,
} from "../lib/theme";
import type { RouterContext } from "../router";
import styleCss from "../styles.css?url";

export const Route = createRootRouteWithContext<RouterContext>()({
  head: () => ({
    links: [{ rel: "stylesheet", href: styleCss }],
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Reliquo" },
      { name: "description", content: "Genealogy platform" },
    ],
  }),
  shellComponent: RootShell,
});

function DarkModeEffect() {
  const { theme } = useTheme();

  createEffect(() => {
    if (isServer) return;

    initializeThemePreference();
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    applyThemePreference(theme(), mq.matches);

    const handler = (e: MediaQueryListEvent) => {
      if (theme() === "system") {
        applyThemePreference("system", e.matches);
      }
    };

    mq.addEventListener("change", handler);
    onCleanup(() => mq.removeEventListener("change", handler));
  });

  return null;
}

/**
 * The RouterProvider context prop requires us to provide context values at render
 * time. We bridge the AuthProvider into the router context via this inner component.
 */
function RouterContextBridge() {
  const auth = useAuth();
  const router = useRouter();

  // Provide auth to all route beforeLoad / loader contexts
  createEffect(() => {
    router.update({ context: { ...router.options.context, auth } });
  });

  return null;
}

function RootShell() {
  const router = useRouter();
  const queryClient = router.options.context.queryClient;

  const getEnv = () => {
    if (typeof window === "undefined") {
      const envSource = typeof process !== "undefined" ? process.env : {};
      return {
        VITE_APP_TITLE: envSource.VITE_APP_TITLE || "Reliquo",
        VITE_OIDC_AUTHORITY: envSource.VITE_OIDC_AUTHORITY,
        VITE_OIDC_CLIENT_ID: envSource.VITE_OIDC_CLIENT_ID,
        VITE_OIDC_REDIRECT_URI: envSource.VITE_OIDC_REDIRECT_URI,
        VITE_API_BASE_URL: envSource.VITE_API_BASE_URL,
        VITE_RELIQUO_ENV: envSource.VITE_RELIQUO_ENV,
      };
    }
    return (window as unknown as { __ENV__: Record<string, string> }).__ENV__;
  };

  return (
    <html lang="en">
      <head>
        <script innerHTML={THEME_INIT_SCRIPT} />
        <HydrationScript />
        <HeadContent />
        <script innerHTML={`window.__ENV__ = ${JSON.stringify(getEnv())}`} />
      </head>
      <body>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <RouterContextBridge />
            <DarkModeEffect />
            <Suspense>
              <Outlet />
            </Suspense>
            <TanStackRouterDevtools />
            <SolidQueryDevtools buttonPosition="bottom-right" />
          </AuthProvider>
        </QueryClientProvider>
        <Scripts />
      </body>
    </html>
  );
}
